import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import {
  Gem, Swords, Package, Gift, Battery, Edit3, Sparkles, Loader2,
  Plus, Minus, Info, PawPrint, Car, Flag, Crown,
} from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { themeOf, ARENA, SLOT_THEME, rarityOf } from '../lib/classTheme';
import {
  ArenaFloor, Pedestal, Laurel, ArenaTitle, ArenaTabs,
  ArenaStatBar, GoldFrame, Torch, ProgressRing,
} from '../components/ArenaFrame';
import { Sheet, SkeletonList } from '../components/ui';

const STAT_COLORS = {
  strength: '#c0392b', agility: '#27ae60', vitality: '#c0392b',
  intellect: '#2980b9', spirit: '#16a085', charisma: '#d63384',
  luck: '#c9973a', endurance: '#d35400', precision_: '#8e44ad',
  defense: '#7a6749', speed: '#00a8a8', willpower: '#b5179e',
};

export default function HeroPage() {
  const qc = useQueryClient();
  const { user, load } = useAuth();
  const [alloc, setAlloc] = useState({});
  const [buyAlloc, setBuyAlloc] = useState({});
  const [tab, setTab] = useState('stats');
  const [swapOpen, setSwapOpen] = useState(false);

  const { data: hero, isLoading } = useQuery({ queryKey: ['hero'], queryFn: () => api.get('/hero').then(r => r.data) });
  const { data: meta } = useQuery({ queryKey: ['heroMeta'], queryFn: () => api.get('/hero/meta').then(r => r.data) });
  const { data: daily } = useQuery({ queryKey: ['daily'], queryFn: () => api.get('/hero/daily/status').then(r => r.data) });
  const { data: limits } = useQuery({ queryKey: ['limits'], queryFn: () => api.get('/hero/limits').then(r => r.data) });
  const { data: petData } = useQuery({ queryKey: ['pet'], queryFn: () => api.get('/pets').then(r => r.data) });
  const { data: carData } = useQuery({ queryKey: ['car'], queryFn: () => api.get('/cars').then(r => r.data) });

  const refresh = () => { qc.invalidateQueries(['hero', 'inventory', 'pet', 'car']); load(); };

  const allocM = useMutation({
    mutationFn: () => api.post('/hero/allocate', { allocation: alloc }),
    onSuccess: () => { toast.success('Stats forged! 💪'); setAlloc({}); refresh(); },
    onError: e => toast.error(e.message),
  });

  const buyM = useMutation({
    mutationFn: () => api.post('/hero/buy-stats-bulk', { allocation: buyAlloc }),
    onSuccess: ({ data: d }) => { toast.success(`+${d.statsAdded} stats · −💎${d.spent}`); setBuyAlloc({}); refresh(); },
    onError: e => toast.error(e.message),
  });

  const dailyM = useMutation({
    mutationFn: () => api.post('/hero/daily'),
    onSuccess: ({ data: d }) => {
      toast.success(`Day ${d.streak} · +${d.xp} XP`);
      if (d.levelUp) toast.success(`⭐ Level ${d.levelUp.to}! +${d.levelUp.statPoints} points`, { duration: 5000 });
      qc.invalidateQueries(['daily', 'hero']); load();
    },
    onError: e => toast.error(e.message),
  });

  const renameM = useMutation({
    mutationFn: (name) => api.post('/hero/rename', { name }),
    onSuccess: () => { toast.success('Renamed!'); qc.invalidateQueries(['hero']); },
  });

  const totalAlloc = Object.values(alloc).reduce((a, b) => a + (b || 0), 0);
  const totalBuy = Object.values(buyAlloc).reduce((a, b) => a + (b || 0), 0);
  const buyCost = totalBuy * (meta?.statPointCost || 1);
  const freeLeft = (hero?.stat_points || 0) - totalAlloc;

  if (isLoading || !hero || !meta) return <SkeletonList n={4} h="h-40" />;

  const t = themeOf(hero.class_key);
  const xp = hero.xp_progress;
  const xpPct = Math.min(100, (xp.current / xp.needed) * 100);

  return (
    <div className="space-y-4 animate-fade-up">
      {/* ═══════ HERO PEDESTAL ═══════ */}
      <div className="card-arena arena-arches overflow-hidden">
        <ArenaFloor classKey={hero.class_key} className="p-5" showCrowd={false}>
          <div className="flex gap-4">
            <Pedestal glow={t.hex}>
              <motion.div className="w-28 h-40 sm:w-32 sm:h-44 rounded-xl overflow-hidden"
                style={{ padding: 2.5, background: ARENA.goldFrame, boxShadow: t.glowCss }}
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}>
                <div className="w-full h-full rounded-[10px] overflow-hidden bg-stone-900">
                  {hero.image_url
                    ? <img src={hero.image_url} alt={hero.name} className="w-full h-full object-cover object-top" />
                    : <div className="w-full h-full grid place-items-center text-3xl">{t.icon}</div>}
                </div>
              </motion.div>
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full
                font-display font-black text-[10px] text-white z-20"
                style={{ background: ARENA.goldFrame, boxShadow: '0 2px 10px -2px rgba(133,96,31,.7)' }}>
                LVL {hero.level}
              </div>
            </Pedestal>

            <div className="flex-1 min-w-0 text-white">
              <div className="flex items-center gap-1.5 mb-1">
                <Laurel size={22} color={ARENA.goldL} />
                <span className="text-[9px] font-display font-black uppercase tracking-[0.22em]"
                  style={{ color: ARENA.goldL }}>{t.romanTitle}</span>
              </div>

              <button onClick={() => { const n = prompt('Hero name', hero.name); if (n) renameM.mutate(n); }}
                className="flex items-center gap-1.5 group">
                <h1 className="font-display text-xl sm:text-2xl font-extrabold tracking-wide truncate"
                  style={{ color: ARENA.goldL, textShadow: '0 2px 14px rgba(201,151,58,.45)' }}>
                  {hero.name}
                </h1>
                <Edit3 size={12} className="opacity-0 group-hover:opacity-60 transition"
                  style={{ color: ARENA.goldL }} />
              </button>

              <p className="text-[11px] font-semibold italic" style={{ color: 'rgba(232,220,200,.65)' }}>
                {t.icon} {t.name} · {t.latin}
              </p>

              <div className="mt-3">
                <div className="flex justify-between text-[10px] font-display font-bold mb-1"
                  style={{ color: ARENA.goldL }}>
                  <span>XP {compact(xp.current)} / {compact(xp.needed)}</span>
                  <span>→ {hero.level + 1}</span>
                </div>
                <ArenaStatBar value={xpPct} max={100} height={7} />
              </div>

              <div className="grid grid-cols-4 gap-1.5 mt-3">
                <Mini l="POWER" v={hero.power} />
                <Mini l="GEAR"  v={hero.gear_score} />
                <Mini l="ELO"   v={hero.elo} />
                <Mini l="W/L"   v={`${hero.wins}/${hero.losses}`} />
              </div>
            </div>
          </div>
        </ArenaFloor>

        {/* ═══ COMPANIONS ROW ═══ */}
        <div className="grid grid-cols-2 gap-px" style={{ background: 'var(--stone-200)' }}>
          <CompanionSlot type="pet" data={petData?.pet} theme={t} to="/pet" />
          <CompanionSlot type="car" data={carData?.car} theme={t} to="/garage" />
        </div>

        {/* ═══ ACTIONS ═══ */}
        <div className="p-3 grid grid-cols-3 gap-2" style={{ background: 'var(--sand-100)' }}>
          <Link to="/inventory" className="btn-ghost btn-sm flex-col !py-2.5 h-auto">
            <Package size={15} />
            <span className="text-[10px] font-display">Armory {hero.equipped_count}/5</span>
          </Link>
          <Link to="/arena" className="btn-primary btn-sm flex-col !py-2.5 h-auto">
            <Swords size={15} /><span className="text-[10px] font-display">Arena</span>
          </Link>
          <button onClick={() => dailyM.mutate()} disabled={daily?.claimed || dailyM.isPending}
            className={`btn-sm flex-col !py-2.5 h-auto ${daily?.claimed ? 'btn-ghost opacity-50' : 'btn-mint'}`}>
            <Gift size={15} />
            <span className="text-[10px] font-display">
              {daily?.claimed ? `🔥${daily.streak}` : `+${daily?.nextXp || 30} XP`}
            </span>
          </button>
        </div>
      </div>

      {/* ═══════ ENERGY ═══════ */}
      {hero.energy && (
        <div className="card p-3 flex items-center gap-3">
          <ProgressRing value={hero.energy.energy} max={hero.energy.max} size={44}
            color="#059669" icon={<Battery size={16} style={{ color: '#059669' }} />} />
          <div className="flex-1">
            <div className="flex justify-between text-xs font-display font-bold mb-1">
              <span>Arena stamina</span>
              <span>{hero.energy.energy}/{hero.energy.max}</span>
            </div>
            <ArenaStatBar value={hero.energy.energy} max={hero.energy.max}
              color="linear-gradient(90deg,#34d399,#059669)" height={7} />
          </div>
          {hero.energy.nextIn > 0 && (
            <span className="text-[11px] font-semibold shrink-0" style={{ color: 'var(--stone-400)' }}>
              +1 in {hero.energy.nextIn}m
            </span>
          )}
        </div>
      )}

      {/* ═══════ TABS ═══════ */}
      <ArenaTabs value={tab} onChange={setTab} tabs={[
        { key: 'stats', label: 'Virtues', icon: '⚡' },
        { key: 'gear',  label: 'Equipment', icon: '🛡️' },
        { key: 'moves', label: 'Techniques', icon: '⚔️' },
      ]} />

      {/* ═══════ STATS ═══════ */}
      {tab === 'stats' && (
        <>
          {hero.stat_points > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
              className="card p-4" style={{ borderColor: ARENA.gold, borderWidth: 2 }}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <Sparkles size={17} style={{ color: ARENA.gold }} />
                  <span className="font-display font-extrabold text-sm">
                    {freeLeft} free virtue points
                  </span>
                </div>
                {totalAlloc > 0 && (
                  <button onClick={() => allocM.mutate()} disabled={allocM.isPending}
                    className="btn-primary btn-sm">
                    {allocM.isPending ? <Loader2 size={12} className="animate-spin" /> : `Forge ${totalAlloc}`}
                  </button>
                )}
              </div>
              <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-500)' }}>
                Earned through battle. Distribute them freely.
              </p>
            </motion.div>
          )}

          <div className="card p-4" style={{ borderColor: '#34d399', borderWidth: 2 }}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <Gem size={17} className="text-mint-600" />
                <span className="font-display font-extrabold text-sm">1 point = 1 virtue</span>
              </div>
              {totalBuy > 0 && (
                <button onClick={() => buyM.mutate()}
                  disabled={buyM.isPending || buyCost > user.points_balance}
                  className="btn-mint btn-sm">
                  {buyM.isPending ? <Loader2 size={12} className="animate-spin" />
                    : `Buy ${totalBuy} · 💎${compact(buyCost)}`}
                </button>
              )}
            </div>
            <p className="text-[11px] font-semibold" style={{ color: 'var(--stone-500)' }}>
              Treasury: <b style={{ color: ARENA.goldD }}>💎 {compact(user.points_balance)}</b> ·
              earned by trading with real merchants
            </p>
            {buyCost > user.points_balance && (
              <p className="text-[11px] font-bold mt-1" style={{ color: ARENA.blood }}>
                Need {compact(buyCost - user.points_balance)} more points
              </p>
            )}
          </div>

          {/* stat list */}
          <div className="card p-4 space-y-2.5">
            {meta.statKeys.map(k => {
              const m = meta.stats[k];
              const base = hero.base_stats[k];
              const eq = hero.equipment_stats[k] || 0;
              const pending = (alloc[k] || 0) + (buyAlloc[k] || 0);
              const total = base + eq + pending;

              return (
                <div key={k} className="flex items-center gap-3">
                  <span className="text-base w-6 text-center shrink-0">{m.icon}</span>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm font-display font-bold">{m.label}</span>
                      <span className="text-sm font-display font-black tabular-nums">
                        {base}
                        {eq > 0 && <span className="text-mint-600 text-xs"> +{eq}</span>}
                        {pending > 0 && <span className="text-xs" style={{ color: ARENA.goldD }}> +{pending}</span>}
                      </span>
                    </div>
                    <div className="mt-1">
                      <ArenaStatBar value={total} max={150} height={7}
                        color={`linear-gradient(90deg, ${STAT_COLORS[k]}, ${STAT_COLORS[k]}cc)`} />
                    </div>
                    <p className="text-[10px] mt-0.5" style={{ color: 'var(--stone-400)' }}>{m.desc}</p>
                  </div>

                  <div className="flex gap-1 shrink-0">
                    {freeLeft > 0 && (
                      <button onClick={() => setAlloc({ ...alloc, [k]: (alloc[k] || 0) + 1 })}
                        title="Use free point"
                        className="w-7 h-7 rounded-lg grid place-items-center text-white active:scale-90 transition"
                        style={{ background: ARENA.goldFrame }}>
                        <Plus size={13} strokeWidth={3} />
                      </button>
                    )}
                    {(alloc[k] > 0 || buyAlloc[k] > 0) && (
                      <button onClick={() => {
                        if (alloc[k] > 0) setAlloc({ ...alloc, [k]: alloc[k] - 1 });
                        else setBuyAlloc({ ...buyAlloc, [k]: buyAlloc[k] - 1 });
                      }}
                        className="w-7 h-7 rounded-lg grid place-items-center"
                        style={{ background: 'var(--stone-200)', color: 'var(--stone-600)' }}>
                        <Minus size={13} strokeWidth={3} />
                      </button>
                    )}
                    <button onClick={() => setBuyAlloc({ ...buyAlloc, [k]: (buyAlloc[k] || 0) + 1 })}
                      title="Buy with points"
                      className="w-7 h-7 rounded-lg grid place-items-center text-white
                        bg-mint-500 active:scale-90 transition">
                      <Gem size={11} />
                    </button>
                  </div>
                </div>
              );
            })}

            <div className="flex gap-1.5 pt-2" style={{ borderTop: '1px solid var(--stone-200)' }}>
              {[10, 50, 100].map(n => (
                <button key={n}
                  onClick={() => {
                    const per = Math.floor(n / meta.statKeys.length) || 1;
                    setBuyAlloc(Object.fromEntries(meta.statKeys.map(k => [k, (buyAlloc[k] || 0) + per])));
                  }}
                  disabled={n > user.points_balance}
                  className="btn-ghost btn-sm flex-1">+{n} spread</button>
              ))}
              {(totalBuy > 0 || totalAlloc > 0) && (
                <button onClick={() => { setBuyAlloc({}); setAlloc({}); }}
                  className="btn-ghost btn-sm" style={{ color: ARENA.blood }}>Clear</button>
              )}
            </div>
          </div>

          {limits && (
            <div className="card p-3.5">
              <ArenaTitle title="Today's limits" latin="Limites Diei" />
              <div className="grid grid-cols-2 gap-2">
                <Limit l="Arena points" u={limits.fights.used} m={limits.fights.max} />
                <Limit l="Race points"  u={limits.races?.used || 0} m={limits.races?.max || 15} />
                <Limit l="Share points" u={limits.shares.used} m={limits.shares.max} />
                <Limit l="Trivia rounds" u={limits.trivia.rounds} m={3} />
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══════ GEAR ═══════ */}
      {tab === 'gear' && (
        <>
          <EquipmentDisplay hero={hero} theme={t} />
          <div className="grid grid-cols-2 gap-2">
            <Link to="/inventory" className="btn-ghost"><Package size={15} /> Armory</Link>
            <Link to="/item-shop" className="btn-primary"><Gem size={15} /> Market</Link>
          </div>
          <button onClick={() => setSwapOpen(true)} className="btn-ghost w-full">
            <Sparkles size={15} /> Change appearance · 💎{compact(meta.rerollCost)}
          </button>
        </>
      )}

      {/* ═══════ MOVES ═══════ */}
      {tab === 'moves' && (
        <div className="card p-4 space-y-2">
          {hero.moves.map(m => (
            <div key={m.key} className="flex gap-3 p-3 rounded-xl"
              style={{ background: 'var(--sand-100)', border: '1px solid var(--stone-200)' }}>
              <span className="text-2xl shrink-0">{m.icon}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-extrabold text-sm">{m.name}</span>
                  {m.focusCost > 0 && <span className="chip-grape">🌀 {m.focusCost}</span>}
                  {m.stamina > 0 && <span className="chip-ink">⚡ {m.stamina}</span>}
                  {m.stamina < 0 && <span className="chip-mint">+{-m.stamina} STA</span>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: 'var(--stone-500)' }}>{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      <SwapAppearance open={swapOpen} onClose={() => setSwapOpen(false)}
        hero={hero} onSuccess={refresh} />
    </div>
  );
}

/* ---------------- COMPANION SLOT ---------------- */
function CompanionSlot({ type, data, theme, to }) {
  const isPet = type === 'pet';
  const Icon = isPet ? PawPrint : Car;
  const label = isPet ? 'Companion' : 'Chariot';

  if (!data) {
    return (
      <Link to={to} className="p-3 flex items-center gap-2.5 transition hover:bg-stone-100"
        style={{ background: 'var(--marble)' }}>
        <div className="w-10 h-10 rounded-xl grid place-items-center border-2 border-dashed"
          style={{ borderColor: 'var(--stone-300)', color: 'var(--stone-300)' }}>
          <Icon size={17} />
        </div>
        <div className="min-w-0">
          <p className="text-[11px] font-display font-bold" style={{ color: 'var(--stone-600)' }}>
            No {label.toLowerCase()}
          </p>
          <p className="text-[9px] font-semibold" style={{ color: 'var(--stone-400)' }}>
            Tap to acquire
          </p>
        </div>
      </Link>
    );
  }

  return (
    <Link to={to} className="p-3 flex items-center gap-2.5 transition hover:bg-stone-100"
      style={{ background: 'var(--marble)' }}>
      <div className="w-10 h-10 rounded-xl overflow-hidden shrink-0"
        style={{ padding: 1.5, background: ARENA.goldFrame }}>
        <div className="w-full h-full rounded-lg overflow-hidden bg-stone-200">
          <img src={data.thumb || data.image} alt="" className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-display font-bold truncate">{data.name}</p>
        <p className="text-[9px] font-semibold" style={{ color: 'var(--stone-400)' }}>
          Lv{data.level} · {isPet ? `${data.gearScore} GS` : `${data.tuningScore} TS`}
        </p>
      </div>
      <Icon size={14} style={{ color: theme.hex }} />
    </Link>
  );
}

/* ---------------- EQUIPMENT DISPLAY ---------------- */
function EquipmentDisplay({ hero, theme }) {
  const slots = hero.equipment || {};
  return (
    <div className="card overflow-hidden">
      <ArenaFloor classKey={hero.class_key} className="p-4" showCrowd={false}>
        <div className="flex items-center gap-3">
          <div className="flex flex-col gap-2">
            {['helmet', 'chest', 'legs'].map(s => <GearSlot key={s} slot={s} item={slots[s]} />)}
          </div>

          <div className="flex-1">
            <Pedestal glow={theme.hex}>
              <div className="w-full max-w-[130px] mx-auto rounded-xl overflow-hidden"
                style={{ padding: 2, background: ARENA.goldFrame }}>
                <div className="rounded-[10px] overflow-hidden bg-stone-900" style={{ aspectRatio: '9/13' }}>
                  {hero.image_url
                    ? <img src={hero.image_url} alt="" className="w-full h-full object-cover object-top" />
                    : <div className="w-full h-full grid place-items-center text-3xl">{theme.icon}</div>}
                </div>
              </div>
            </Pedestal>
            <p className="text-center text-[10px] font-display font-bold mt-3"
              style={{ color: ARENA.goldL }}>
              {hero.gear_score} GEAR SCORE
            </p>
          </div>

          <div className="flex flex-col gap-2">
            {['weapon', 'boots'].map(s => <GearSlot key={s} slot={s} item={slots[s]} />)}
            <Link to="/item-shop"
              className="w-[58px] h-[58px] rounded-xl grid place-items-center border-2 border-dashed
                transition hover:scale-105"
              style={{ borderColor: 'rgba(232,200,116,.35)' }}>
              <Gem size={16} style={{ color: ARENA.goldL, opacity: .6 }} />
            </Link>
          </div>
        </div>
      </ArenaFloor>

      {hero.equipment_stats && Object.values(hero.equipment_stats).some(v => v > 0) && (
        <div className="p-3" style={{ background: 'var(--sand-100)' }}>
          <p className="label">Equipment bonuses</p>
          <div className="flex flex-wrap gap-1">
            {Object.entries(hero.equipment_stats)
              .filter(([, v]) => v > 0)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <span key={k} className="chip-mint">+{v} {k.replace('_', '').slice(0, 4)}</span>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}

function GearSlot({ slot, item }) {
  const s = SLOT_THEME[slot];
  const r = item ? rarityOf(item.rarity) : null;
  return (
    <Link to="/inventory" className="relative">
      <div className="w-[58px] h-[58px] rounded-xl overflow-hidden transition hover:scale-105"
        style={item
          ? { padding: 2, background: `linear-gradient(135deg, ${r.hex}, ${r.glow})`, boxShadow: r.shadowCss }
          : { border: '2px dashed rgba(232,200,116,.3)' }}>
        {item ? (
          <div className="w-full h-full rounded-lg overflow-hidden bg-stone-900">
            <img src={item.thumb || item.image} alt="" className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="w-full h-full grid place-items-center text-lg opacity-30">{s.icon}</div>
        )}
      </div>
      <p className="text-[7px] font-display font-black text-center mt-0.5 tracking-wider"
        style={{ color: 'rgba(232,220,200,.55)' }}>{s.latin}</p>
    </Link>
  );
}

/* ---------------- helpers ---------------- */
function Mini({ l, v }) {
  return (
    <div className="rounded-lg px-2 py-1.5 text-center"
      style={{ background: 'rgba(232,200,116,.14)', border: '1px solid rgba(232,200,116,.22)' }}>
      <p className="text-[8px] font-display font-bold tracking-wider"
        style={{ color: 'rgba(232,220,200,.6)' }}>{l}</p>
      <p className="font-display font-black text-sm" style={{ color: ARENA.goldL }}>{v}</p>
    </div>
  );
}

function Limit({ l, u, m }) {
  const pct = m ? Math.min(100, (u / m) * 100) : 0;
  return (
    <div className="rounded-xl p-2" style={{ background: 'var(--sand-100)' }}>
      <div className="flex justify-between text-[10px] font-bold mb-1">
        <span style={{ color: 'var(--stone-500)' }}>{l}</span>
        <span style={{ color: pct >= 100 ? ARENA.blood : 'var(--stone-600)' }}>{u}/{m}</span>
      </div>
      <ArenaStatBar value={u} max={m} height={5}
        color={pct >= 100 ? '#8b2635' : ARENA.goldFrame} />
    </div>
  );
}

function SwapAppearance({ open, onClose, hero, onSuccess }) {
  const [sel, setSel] = useState(null);
  const { data } = useQuery({
    queryKey: ['heroVariantsOwn'],
    queryFn: () => api.get('/hero/variants').then(r => r.data),
    enabled: open,
  });

  const swap = useMutation({
    mutationFn: () => api.post('/hero/swap-appearance', { templateId: sel }),
    onSuccess: () => { toast.success('New form taken! ✨'); onClose(); onSuccess(); },
    onError: e => toast.error(e.message),
  });

  const t = themeOf(hero?.class_key);

  return (
    <Sheet open={open} onClose={onClose} title="Change appearance" wide>
      <p className="text-sm mb-3" style={{ color: 'var(--stone-500)' }}>
        Choose another form for your <b>{hero?.class?.name}</b>. Virtues and gear remain.
      </p>

      <div className="grid grid-cols-3 gap-2.5">
        {data?.variants?.map(v => (
          <button key={v.id} onClick={() => !v.isCurrent && setSel(v.id)} disabled={v.isCurrent}
            className={`relative rounded-xl overflow-hidden transition
              ${v.isCurrent ? 'opacity-50' : sel === v.id ? 'scale-105' : 'hover:scale-[1.02]'}`}
            style={{
              padding: 2,
              background: sel === v.id || v.isCurrent ? ARENA.goldFrame : 'var(--stone-200)',
              boxShadow: sel === v.id ? t.glowCss : 'none',
            }}>
            <div className="rounded-lg overflow-hidden bg-stone-200" style={{ aspectRatio: '9/16' }}>
              <img src={v.thumb_url || v.image_url} alt="" className="w-full h-full object-cover object-top" />
            </div>
            {v.isCurrent && (
              <span className="absolute top-1.5 left-1.5 chip-gold !text-[7px]">CURRENT</span>
            )}
            <div className="absolute inset-x-0 bottom-0 p-1.5"
              style={{ background: 'linear-gradient(180deg, transparent, rgba(36,30,22,.85))' }}>
              <p className="text-[8px] font-display font-black text-white truncate">{v.name}</p>
            </div>
          </button>
        ))}
      </div>

      <button onClick={() => swap.mutate()} disabled={!sel || swap.isPending}
        className="btn-primary w-full mt-4 py-3">
        {swap.isPending ? <><Loader2 size={16} className="animate-spin" /> Transforming…</>
          : <>Apply · 💎{compact(data?.cost || 0)}</>}
      </button>
    </Sheet>
  );
}